import { HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { formatPageTitle } from '@/constants/stringVars';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import queryKeys from '@/constants/queryKeys';
import { contentCreatorRoutes } from '@/constants/routes/app';
import useContentImageUpload from '@/hooks/content/useContentImageUpload';
import useToast from '@/hooks/useToast';
import {
  buildContentDetails,
  collectImageUploads,
  mapContentToFormValues,
} from '@/modules/content/creation/model/contentPayload';
import { TContentFormMode, TContentFormSubmit, WizardFinalAction } from '@/modules/content/creation/model/types';
import ContentWizard from '@/modules/content/creation/ContentWizard';
import svgIcons from '@/assets/svg/icons';
import { TCustomAxiosError } from '@/services/api';
import {
  createMuxUploadUrl,
  getContentById,
  publishContent,
  setYoutubeVideoSource,
  updateContent,
} from '@/services/api/requests/content';
import { uploadFileToMux } from '@/services/muxUpload';
import { useAppStore } from '@/store/useAppStore';
import { extra, text, text as textColors } from '@/theme/colors/palettes';
import roles from '@/constants/roles';
import { parseYouTubeId } from '@/modules/content/creation/model/youtube';
import LogoSpinner from '@/components/atoms/LogoSpinner';

const CreatorContentEdit = () => {
  const { contentId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user, organization } = useAppStore();

  const {
    data: content,
    isLoading,
    isFetchedAfterMount,
    isError,
  } = useQuery({
    queryKey: [queryKeys.content.byId, contentId],
    queryFn: () => getContentById(contentId),
    refetchOnMount: 'always',
  });

  const { uploadThumbnailAsync, uploadArticleImageAsync } = useContentImageUpload();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  const authorLabel = useMemo(() => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.name || 'You';
    return organization?.name ? `${name} | ${organization.name}` : name;
  }, [user, organization]);

  const orgId = user?.app_metadata?.primaryOrganizationId;
  const userRoles = user?.roles ?? [];
  const allowOrganizationVisibility = !(userRoles.includes(roles.contentCreator) && userRoles.length === 1);
  const contentFormDefaults = useMemo(() => {
    if (!content) return undefined;

    const defaults = mapContentToFormValues(content);

    return {
      ...defaults,
      isPublic: !allowOrganizationVisibility || defaults.isPublic,
    };
  }, [allowOrganizationVisibility, content]);

  useEffect(() => {
    if (content?.title) document.title = formatPageTitle(content.title);
  }, [content?.title]);

  const goToList = () => navigate({ to: contentCreatorRoutes.creatorProfile });

  const handleSubmit = async (mode: TContentFormMode, { values, thumbnailFile }: TContentFormSubmit) => {
    if (!content) return;
    const id = content.id;
    const type = content.type;
    setIsSaving(true);
    try {
      await updateContent(id, {
        title: values.title,
        description: values.description,
        domains: values.domains,
        subdomains: values.subdomains,
        visibility:
          !allowOrganizationVisibility || values.isPublic
            ? { public: true, orgIds: [] }
            : { public: false, orgIds: orgId ? [orgId] : [] },
        details: buildContentDetails(type, values),
      });

      if (thumbnailFile) {
        await uploadThumbnailAsync({ id, file: thumbnailFile });
      }

      // Published video sources are immutable. For drafts, preserve the remote
      // source unless the creator explicitly selected a replacement.
      if (type === 'video' && content.status !== 'published') {
        const youtubeChanged =
          values.videoSource === 'youtube' &&
          (values.originalVideoSource !== 'youtube' ||
            parseYouTubeId(values.youtubeUrl) !== parseYouTubeId(values.originalYoutubeUrl ?? ''));

        if (youtubeChanged && values.youtubeUrl.trim()) {
          await setYoutubeVideoSource(id, values.youtubeUrl.trim());
        } else if (values.videoSource === 'mux' && values.videoFile) {
          const { uploadUrl } = await createMuxUploadUrl(id);
          setUploadPercent(0);
          await uploadFileToMux(uploadUrl, values.videoFile, setUploadPercent);
          setUploadPercent(null);
        }
      } else {
        for (const { sectionId, subsectionId, file } of collectImageUploads(values)) {
          await uploadArticleImageAsync({ id, file, sectionId, subsectionId });
        }
      }

      if (mode === 'publish' && content.status !== 'published') {
        await publishContent(id);
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.mine] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, id] });
      toast({
        status: 'success',
        title: 'Success',
        description: mode === 'publish' && content.status !== 'published' ? 'Content published.' : 'Changes saved.',
      });
      goToList();
    } catch (error) {
      setUploadPercent(null);
      toast({
        status: 'error',
        title: 'Error saving content',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // A published item only saves changes; a draft can be published from the preview step.
  const isPublished = content?.status === 'published';
  const primaryAction: WizardFinalAction = isPublished
    ? { key: 'save', label: 'Save changes', mode: 'draft', backgroundColor: 'primary.500' }
    : { key: 'publish', label: 'Publish Content', mode: 'publish', backgroundColor: 'primary.500' };

  return (
    <VStack width={MAX_CONTAINER_WIDTH} gap={'8px'} alignItems={'stretch'}>
      <ModalContainer
        isOpen={uploadPercent !== null}
        onClose={() => {}}
        width={'720px'}
        backgroundColor={extra.white}
        closeOnOverlayClick={false}
        closeButtonHidden
      >
        <VStack paddingX={'32px'} paddingY={'36px'} gap={'18px'} align={'stretch'}>
          <Text variant={'loraMediumTitle'} color={textColors.darkBlue} textAlign={'center'}>
            Uploading your reel
          </Text>
          <Text fontSize={'14px'} color={textColors.mediumBlue} textAlign={'center'}>
            Please keep this tab open until the upload finishes.
          </Text>
          <Progress
            value={uploadPercent ?? 0}
            size={'md'}
            borderRadius={'full'}
            colorScheme={'purple'}
            hasStripe
            isAnimated
          />
          <Text fontSize={'13px'} fontWeight={700} color={textColors.mediumBlue} textAlign={'center'}>
            {uploadPercent ?? 0}%
          </Text>
        </VStack>
      </ModalContainer>

      {isLoading || !isFetchedAfterMount ? (
        <HStack width={'100%'} justifyContent={'center'} paddingY={'60px'}>
          <LogoSpinner />
        </HStack>
      ) : isError || !content ? (
        <>
          <HStack>
            <CustomButton
              isTransparent
              iconBeforeLabelSvgPath={svgIcons.leftArrow}
              iconBeforeLabelSvgBoxHeight={12}
              iconBeforeLabelSvgColor={text.mediumBlue}
              labelColor={'text.mediumBlue'}
              labelHoverColor={'extra.black'}
              label={'Back to Your Content'}
              onClick={goToList}
            />
          </HStack>
          <Text color={'extra.red'} marginTop={'20px'}>
            Couldn’t load this content.
          </Text>
        </>
      ) : (
        <ContentWizard
          key={contentId}
          type={content.type}
          authorLabel={authorLabel}
          defaultValues={contentFormDefaults}
          defaultThumbnailUrl={content.effectiveThumbnailUrl ?? content.thumbnailUrl}
          allowOrganizationVisibility={allowOrganizationVisibility}
          isSubmitting={isSaving}
          onExit={goToList}
          onSubmit={handleSubmit}
          secondaryAction={{ key: 'save', label: 'Save changes', mode: 'draft', isTransparent: true }}
          primaryAction={primaryAction}
          requireChangesToContinue={isPublished}
          isVideoSourceReadOnly={isPublished}
        />
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/_content/creator/content/$contentId')({
  staticData: { title: 'Content' },
  component: CreatorContentEdit,
});
